import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { useOrganizationChapters } from '@data/hooks/organization';
import { useOrganizationId } from '@app/utils/hooks';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Loader } from '@ui';
import { cardHeaderClass, cardHeaderTitleClass, cardShellClass, tableFrameClass } from '@app/constants/styles';
import { SENIORITY_LEVELS } from '../../members/memberMock';
import {
  asAmount,
  COST_COUNTRIES,
  type CostCountry,
  type CostMatrix as CostMatrixValues,
  CostMatrixDraft,
  DEFAULT_COST_COUNTRY,
  draftFromMatrix,
  formatRate,
  invalidCells,
  matrixFromDraft,
  readCostMatrix,
  writeCostMatrix,
} from './costs';
import { useReportUnsaved } from './unsavedWork';
import { numberFieldHolds, rangeContainer, rangeOverlay, useCellRange } from './cellRange';
import CellMenu from './CellMenu';
import SprintScrollRail from './SprintScrollRail';

// A fixed row height, so a cell opening to be typed into does not grow the grid.
const cell = 'relative h-10 border-b border-r border-border last:border-r-0';
const padded = `${cell} px-4 max-md:px-3`;
// The same on both axes, which is what makes them read as the axes of one grid.
const headCell = `${padded} bg-muted/40 text-sm font-medium text-foreground max-md:text-xs`;

/** Part of the block so the whole price list copies at once; neither can be typed into. */
const HEAD_ROW = '__head__';
const NAME_COL = '__chapter__';

/** Prices belong to a version of the terms, so typing into one leaves the others alone. */
const CostMatrix = ({
  canEdit,
  version,
  productId: givenProductId,
  onlyChapters,
  markedCells = [],
}: {
  canEdit: boolean;
  version?: string;
  /** For a matrix shown off the product's own page, which has no product in its address. */
  productId?: string;
  /** The rows to show, where only some chapters matter. What is stored for the others is kept. */
  onlyChapters?: string[];
  /** Empty prices a caller needs filled in, marked until they are. */
  markedCells?: { chapterKey: string; level: string }[];
}): JSX.Element => {
  const { t } = useTranslation();
  const { productId: routeProductId = '' } = useParams<{ productId: string }>();
  const productId = givenProductId ?? routeProductId;
  const organizationId = useOrganizationId() ?? '';
  // Only chapters the org still offers get a cell; a switched-off one's price stays in storage.
  const { data: chapterData, isLoading } = useOrganizationChapters(organizationId);
  const chapters = chapterData?.items ?? [];
  const shownChapters = onlyChapters ? chapters.filter(chapter => onlyChapters.includes(chapter.key)) : chapters;

  // The reader's choice for as long as they are on the tab, not a saved setting.
  const [country, setCountry] = useState<CostCountry>(DEFAULT_COST_COUNTRY);
  // Bumped on save, because the prices live outside React in the product store.
  const [revision, setRevision] = useState(0);
  /** Sparse: a cell absent here reads what is stored, which is what tells changed from untouched. */
  const [draft, setDraft] = useState<CostMatrixDraft>({});
  /** The one cell being typed into, as `chapter:level`: a cell is a price until it is asked for. */
  const [active, setActive] = useState<string | null>(null);

  const matrix = useMemo(() => readCostMatrix(productId, country, version), [productId, country, version, revision]);

  // Typing belongs to the country and version it was typed into.
  useEffect(() => {
    setDraft({});
    setActive(null);
  }, [productId, country, version]);

  const typedAt = (chapterKey: string, level: string): string | undefined => draft[chapterKey]?.[level];
  const stored = (chapterKey: string, level: string): number | null => matrix[chapterKey]?.[level] ?? null;
  /** What the cell holds: what was typed into it, or the price it was already carrying. */
  const shown = (chapterKey: string, level: string): string => {
    const typedValue = typedAt(chapterKey, level);
    if (typedValue !== undefined) return typedValue;
    const amount = stored(chapterKey, level);
    return amount === null ? '' : String(amount);
  };
  // A blank cell is "not priced" rather than nothing an hour costs, so clearing one takes the price out.
  const entered = (chapterKey: string, level: string): number | null => {
    const value = shown(chapterKey, level);
    return value.trim() === '' ? null : asAmount(value);
  };
  const invalid = invalidCells(draft);
  const isInvalid = (chapterKey: string, level: string): boolean => invalid.includes(`${chapterKey}:${level}`);
  /** A cell holding something other than the price it was carrying: what Save would write, and marked. */
  const changed = (chapterKey: string, level: string): boolean =>
    typedAt(chapterKey, level) !== undefined &&
    !isInvalid(chapterKey, level) &&
    entered(chapterKey, level) !== stored(chapterKey, level);

  /** A block of cells at once, which is what a paste and a cleared selection are. */
  const typeMany = (cells: { row: string; col: string; value: string }[]): void =>
    setDraft(current => {
      const next = { ...current };
      cells.forEach(({ row, col, value }) => {
        next[row] = { ...next[row], [col]: value };
      });
      return next;
    });

  /** Read as stored rather than as drawn, so a spreadsheet gets a number and not "€85.00". */
  const range = useCellRange({
    rows: [HEAD_ROW, ...shownChapters.map(chapter => chapter.key)],
    cols: [NAME_COL, ...SENIORITY_LEVELS],
    // Plainly: "€85.00" arrives in a spreadsheet cell as text, and text does not add up.
    readCell: (chapterKey, level) => {
      if (chapterKey === HEAD_ROW) {
        return level === NAME_COL ? t('chapter') : t('cost.levelColumn', { level: t(`seniorityLevels.${level}`) });
      }
      if (level === NAME_COL) return chapters.find(chapter => chapter.key === chapterKey)?.label ?? '';
      return shown(chapterKey, level);
    },
    writeCells: canEdit ? typeMany : undefined,
    // The names and the headings are read: only a price can be typed over or emptied.
    isEditable: (chapterKey, level) => canEdit && chapterKey !== HEAD_ROW && level !== NAME_COL,
    resetOn: `${productId}:${country}:${version ?? ''}`,
    // Every figure here is a price, so a copy taken from it is remembered as money.
    leadIsCost: true,
  });

  /** Stored is the base, not the rows on screen, so a switched-off chapter's price is not dropped. */
  const resolved = ((): CostMatrixValues => {
    const full = draftFromMatrix(matrix);
    Object.entries(draft).forEach(([chapterKey, levels]) => {
      full[chapterKey] = { ...full[chapterKey], ...levels };
    });
    return matrixFromDraft(full);
  })();
  // Compared in the order the grid is drawn, since the keys can be stored in any order.
  const asStored = (values: CostMatrixValues): string =>
    JSON.stringify(chapters.map(chapter => SENIORITY_LEVELS.map(level => values[chapter.key]?.[level] ?? null)));
  const dirty = asStored(resolved) !== asStored(matrix);
  // Typed but unreadable is not a change, so `dirty` alone left the row red with no Discard to clear it.
  const touched = dirty || invalid.length > 0;
  const canSave = dirty && invalid.length === 0;

  // Whatever would take the reader off this tab or this version asks before it costs them this typing.
  useReportUnsaved('costMatrix', touched);

  const discard = (): void => {
    setDraft({});
    setActive(null);
    range.clear();
  };

  const save = (): void => {
    writeCostMatrix(productId, resolved, country, version);
    discard();
    setRevision(current => current + 1);
  };

  /** What a cell needs to be part of the block: the wash where it is in one, and the gestures. */
  const inBlock = (row: string, col: string) => ({
    className: range.selected(row, col) ? rangeOverlay(range.edges(row, col)) : undefined,
    ...range.cellHandlers(row, col),
  });

  const countryOptions = COST_COUNTRIES.map(key => ({ value: key, label: t(`countries.${key}`) }));

  const type = (chapterKey: string, level: string, value: string): void =>
    setDraft(current => ({ ...current, [chapterKey]: { ...current[chapterKey], [level]: value } }));

  /** A price as it reads when nobody is typing into it. Blank where there is none: this cell is a field. */
  const reads = (chapterKey: string, level: string): string => {
    const value = shown(chapterKey, level);
    if (value.trim() === '') return '';
    const amount = asAmount(value);
    // What was typed where it is not a price: hiding it would leave nothing to correct.
    return amount === null ? value : `€${formatRate(amount)}`;
  };

  return (
    <section className={cardShellClass}>
      {/* Inside the card, like the platform costs' own header below: the control belongs to the grid. */}
      <div className={cx(cardHeaderClass, 'print:hidden')}>
        <div className={cardHeaderTitleClass}>
          <h2 className="text-base font-semibold text-foreground">{t('cost.matrix')}</h2>
          {!onlyChapters && <p className="text-sm text-muted-foreground">{t('cost.matrixHint')}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Held while there is typing: the lists are priced one at a time and switching would drop it. */}
          <Select
            value={country}
            onValueChange={next => setCountry(next as CostCountry)}
            items={selectItemsByValue(countryOptions)}
            disabled={touched}
          >
            <SelectTrigger size="sm" className="bg-card shadow-xs" aria-label={t('country')}>
              <SelectValue />
            </SelectTrigger>
            {/* The kit aligns the selected item with the trigger text, which shifts and flips the list. */}
            <SelectContent className="min-w-0" align="start" alignItemWithTrigger={false}>
              {countryOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Held rather than hidden where a price is not a price, so the cell can say why. */}
          {canEdit && touched && (
            <>
              <Button variant="ghost" size="sm" onClick={discard}>
                {t('budget.discard')}
              </Button>
              <Button size="sm" disabled={!canSave} onClick={save}>
                {t('actions.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* The box around the grid holds the selected block: the keys and the clipboard are its own. */}
      {isLoading ? (
        <div className="p-6">
          <Loader />
        </div>
      ) : (
        <div
          ref={range.containerHandlers.ref}
          tabIndex={range.containerHandlers.tabIndex}
          onCopy={active ? undefined : range.containerHandlers.onCopy}
          // A block closes the cell it landed on, so it is read as figures, not through the field.
          onPaste={event => {
            range.containerHandlers.onPaste(event);
            if (event.defaultPrevented) setActive(null);
          }}
          onKeyDown={active ? undefined : range.containerHandlers.onKeyDown}
          className={cx(tableFrameClass, 'no-scrollbar mb-5', rangeContainer)}
        >
          {/* Fixed layout, or the chapter names take every pixel the longest one asks for. */}
          <table className="w-full min-w-[48rem] table-fixed max-md:min-w-[36rem] border-collapse [&>tbody>tr:last-child>*]:border-b-0">
            <thead>
              <tr>
                <th
                  scope="col"
                  {...inBlock(HEAD_ROW, NAME_COL)}
                  className={cx(
                    headCell,
                    'w-[22%] text-left max-md:w-[var(--cost-name-width)]',
                    inBlock(HEAD_ROW, NAME_COL).className,
                  )}
                >
                  {t('chapter')}
                </th>
                {SENIORITY_LEVELS.map(level => (
                  <th
                    key={level}
                    scope="col"
                    {...inBlock(HEAD_ROW, level)}
                    className={cx(headCell, 'text-right', inBlock(HEAD_ROW, level).className)}
                  >
                    {t('cost.levelColumn', { level: t(`seniorityLevels.${level}`) })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownChapters.map(chapter => (
                <tr key={chapter.key}>
                  <th
                    scope="row"
                    {...inBlock(chapter.key, NAME_COL)}
                    className={cx(headCell, 'text-left', inBlock(chapter.key, NAME_COL).className)}
                  >
                    {chapter.label}
                  </th>
                  {SENIORITY_LEVELS.map(level => {
                    const typing = active === `${chapter.key}:${level}`;
                    const open = (): void => {
                      if (!canEdit) return;
                      setActive(`${chapter.key}:${level}`);
                      // The cell being typed into is also where a pasted block would land.
                      range.anchorAt(chapter.key, level);
                    };
                    return (
                      <td
                        key={level}
                        // Focusable, so the grid can be priced from the keyboard alone.
                        tabIndex={typing || !canEdit ? undefined : 0}
                        onFocus={typing ? undefined : open}
                        // A drag or shift-click has already claimed the click for the block.
                        onClick={typing ? undefined : () => !range.claimedClick() && open()}
                        {...(typing
                          ? range.openCellHandlers(chapter.key, level)
                          : range.cellHandlers(chapter.key, level))}
                        onKeyDown={
                          typing
                            ? undefined
                            : event => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                // Space scrolls the page otherwise, and this card is scrolled past.
                                event.preventDefault();
                                open();
                              }
                        }
                        className={cx(
                          typing ? cell : `${padded} text-right`,
                          !typing &&
                            'cursor-text outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                          // An outline, not a fill: it has to mark an emptied cell too.
                          !typing && changed(chapter.key, level) && 'ring-1 ring-inset ring-foreground/30',
                          !typing && isInvalid(chapter.key, level) && 'bg-destructive/10',
                          !typing &&
                            stored(chapter.key, level) === null &&
                            markedCells.some(cell => cell.chapterKey === chapter.key && cell.level === level) &&
                            'bg-amber-500/10',
                          !typing &&
                            range.selected(chapter.key, level) &&
                            rangeOverlay(range.edges(chapter.key, level)),
                          typing &&
                            range.selected(chapter.key, level) &&
                            rangeOverlay(range.edges(chapter.key, level), { open: true }),
                        )}
                      >
                        {typing ? (
                          <Input
                            {...(numberFieldHolds(shown(chapter.key, level))
                              ? { type: 'number', min: 0, step: '0.01' }
                              : { type: 'text', inputMode: 'decimal' as const })}
                            aria-label={`${chapter.label}, ${t(`seniorityLevels.${level}`)}`}
                            // Tells a cell nobody has priced from one that does not apply.
                            placeholder={t('cost.ratePlaceholder')}
                            value={shown(chapter.key, level)}
                            autoFocus
                            onBlur={() => setActive(null)}
                            // The way back out to the grid: while a cell is open the field owns the keys.
                            onKeyDown={event => {
                              if (event.key !== 'Escape' && event.key !== 'Enter') return;
                              event.preventDefault();
                              event.currentTarget.blur();
                            }}
                            // A number field takes the wheel as input, and this card is scrolled past.
                            onWheel={event => event.currentTarget.blur()}
                            onChange={event => type(chapter.key, level, event.target.value)}
                            // Exclusive fills: two Tailwind backgrounds are settled by emit order, not by this one.
                            className={cx(
                              'h-full rounded-none border-0 px-4 text-right',
                              // Inside its own cell: the kit rings 3px outwards, over the rows either side.
                              'focus-visible:ring-2 focus-visible:ring-inset',
                              // No stepper: the arrows sit on top of the number in a cell this narrow.
                              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                              isInvalid(chapter.key, level)
                                ? 'bg-destructive/10 hover:bg-destructive/15'
                                : 'bg-muted/20 hover:bg-muted/40',
                            )}
                          />
                        ) : (
                          <span
                            className={cx(
                              'text-sm',
                              changed(chapter.key, level) ? 'font-medium text-foreground' : 'text-muted-foreground',
                            )}
                          >
                            {reads(chapter.key, level)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <SprintScrollRail scroller={range.containerHandlers.ref} columns={0} />
        </div>
      )}

      {/* Right-clicking the block, as on the workforce grid. One figure to a cell here, so one Copy. */}
      <CellMenu
        at={range.menuAt}
        onClose={range.closeMenu}
        items={[
          { label: t('cells.copy'), onSelect: () => range.copyAs('lead') },
          ...(range.hasEditable() ? [{ label: t('cells.clear'), onSelect: () => range.clearCells() }] : []),
        ]}
      />

      {range.costPasteRefused && (
        <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.costPasteRefused')}</p>
      )}
      {range.copyFailed && <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.copyFailed')}</p>}
    </section>
  );
};

export default CostMatrix;
